/**
 * The one area filter, shared by tasks and reports (RESP2e).
 *
 * ## Why a checkbox list and not the async combobox
 *
 * `AsyncMultiCombobox` is the repo's other multi-select, and it is
 * built for a set nobody can hold in their head — it pages a server as
 * you type. An organisation has thirteen system areas plus a handful of
 * its own. That fits on screen, so the whole list is on screen, and
 * picking three of them costs three clicks instead of three searches.
 * `LanguageMultiSelect` is the existing precedent for exactly this
 * shape.
 *
 * ## Retired areas are OFFERED here
 *
 * This is the one place in the product where that is true, and it looks
 * like a bug until you need it: retiring "Reinigung" must not make last
 * March's cleaning work unfindable. So a form may not offer a retired
 * area (RESP2d) and a filter must — same catalog, opposite rule. The
 * component reads `allCategories` for that reason, never
 * `activeCategories`, and the two are separate on the hook precisely so
 * this choice has to be made deliberately.
 *
 * They are marked with a word, not a colour: a screen reader has to
 * hear the difference, and "greyed out" would suggest unselectable,
 * which is the opposite of true.
 *
 * ## "Ohne Bereich" is an option, not a checkbox somewhere else
 *
 * It answers the same question — which areas? — so it belongs in the
 * same list. It sits first because it is the one entry that is not an
 * area, and burying it between two alphabetically-adjacent areas would
 * hide it.
 */
import React, { useMemo, useState } from "react";

import { Checkbox } from "../../../components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "../../../components/ui/drawer";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { areaIcon } from "../responsibilities/areaPresentation";
import useActivityCategoryCatalog from "./useActivityCategoryCatalog";

/** Shown on the trigger when nothing is selected. */
export const ALL_AREAS_LABEL = "Alle Bereiche";

/** The synthetic option for rows with no area at all. */
export const UNASSIGNED_OPTION_LABEL = "Ohne Bereich";

/** Suffix marking a retired area, as a word rather than a colour. */
export const INACTIVE_SUFFIX = "Inaktiv";

/**
 * What the closed trigger says.
 *
 * One selection is named; several are counted, because five area names
 * in a filter bar push everything else off the row. The count is the
 * summary, the open panel is the detail.
 */
export function triggerLabel({ categories, unassigned, labelFor }) {
  const parts = (categories || []).map(labelFor);
  if (unassigned) parts.push(UNASSIGNED_OPTION_LABEL);
  if (parts.length === 0) return ALL_AREAS_LABEL;
  if (parts.length === 1) return parts[0];
  return `${parts.length} Bereiche`;
}

function OptionRow({ id, checked, onCheckedChange, icon, label, inactive }) {
  const Icon = icon ? areaIcon(icon) : null;
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2.5 rounded-[8px] px-2 py-1.5 hover:bg-muted"
    >
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} />
      {Icon ? (
        <Icon
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          strokeWidth={1.75}
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
        {label}
      </span>
      {inactive ? (
        // Part of the label's accessible text, not a decoration beside
        // it — the distinction has to survive being read aloud.
        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {INACTIVE_SUFFIX}
        </span>
      ) : null}
    </label>
  );
}

function FilterBody({ categories, unassigned, onChange, options, idPrefix }) {
  const selected = useMemo(() => new Set(categories), [categories]);

  const toggleCategory = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // Re-emitted in CATALOG order, not click order, so the URL for a
    // given selection is always the same string however it was reached.
    onChange(
      options.filter((o) => next.has(o.activity_category)).map(
        (o) => o.activity_category
      ),
      unassigned
    );
  };

  return (
    <div className="space-y-0.5">
      <OptionRow
        id={`${idPrefix}-unassigned`}
        checked={unassigned}
        onCheckedChange={() => onChange(categories, !unassigned)}
        icon={null}
        label={UNASSIGNED_OPTION_LABEL}
        inactive={false}
      />
      <div className="my-1 h-px bg-border" role="none" />
      {options.map((option) => (
        <OptionRow
          key={option.activity_category}
          id={`${idPrefix}-${option.activity_category}`}
          checked={selected.has(option.activity_category)}
          onCheckedChange={() => toggleCategory(option.activity_category)}
          icon={option.icon}
          label={option.label}
          inactive={option.is_active === false}
        />
      ))}
    </div>
  );
}

export default function ActivityCategoryFilter({
  // Defaulted because this is handed a `filters` bag assembled by a
  // hook, and a presentational component must not crash when a key it
  // did not exist for yet is absent — several report suites build that
  // bag by hand.
  categories = [],
  unassigned = false,
  onChange,
  onClear,
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const { allCategories, loading, error, refresh, getCategoryMeta } =
    useActivityCategoryCatalog();

  const labelFor = (key) => getCategoryMeta(key)?.label || key;
  const label = triggerLabel({ categories, unassigned, labelFor });
  const active = categories.length > 0 || unassigned;

  const trigger = (
    <button
      type="button"
      onClick={isMobile ? () => setOpen(true) : undefined}
      disabled={loading && !allCategories.length}
      aria-label="Bereich filtern"
      aria-expanded={open}
      className={
        "inline-flex h-[38px] w-full items-center justify-between gap-2 rounded-[8px] border px-3 text-[13px] sm:w-[220px] " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 " +
        (active
          ? "border-violet-500 bg-violet-500/10 text-foreground"
          : "border-border bg-background text-foreground hover:bg-muted")
      }
    >
      <span className="truncate">
        {loading && !allCategories.length ? "Wird geladen …" : label}
      </span>
      <span aria-hidden="true" className="shrink-0 text-muted-foreground">
        ▾
      </span>
    </button>
  );

  if (error) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          disabled
          aria-label="Bereich filtern"
          className="inline-flex h-[38px] w-full items-center rounded-[8px] border border-border bg-background px-3 text-[13px] text-muted-foreground sm:w-[220px]"
        >
          Bereiche nicht verfügbar
        </button>
        <p role="alert" className="m-0 text-[12px] text-destructive">
          {error}{" "}
          <button
            type="button"
            onClick={() => refresh()}
            className="underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </p>
      </div>
    );
  }

  const body = (
    <FilterBody
      categories={categories}
      unassigned={unassigned}
      onChange={onChange}
      options={allCategories}
      idPrefix={isMobile ? "acf-m" : "acf-d"}
    />
  );

  const clearRow = active ? (
    <div className="mt-2 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => {
          onClear();
          setOpen(false);
        }}
        aria-label="Bereichsfilter zurücksetzen"
        className="w-full rounded-[8px] px-2 py-1.5 text-left text-[12px] font-medium text-violet-700 hover:bg-muted dark:text-violet-300"
      >
        Bereichsfilter zurücksetzen
      </button>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Bereich filtern</DrawerTitle>
              <DrawerDescription>
                Mehrere Bereiche auswählbar. Inaktive Bereiche bleiben
                wählbar, damit ältere Einträge auffindbar sind.
              </DrawerDescription>
            </DrawerHeader>
            {/* The drawer's own scroll region; the list can be long. */}
            <div className="max-h-[60vh] overflow-y-auto px-4 pb-6">
              {body}
              {clearRow}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-2">
        {/* Capped and scrollable: thirteen system areas plus an
            organisation's own can exceed a short viewport, and a
            popover that runs off the screen has no scrollbar of its
            own. */}
        <div className="max-h-[320px] overflow-y-auto pr-1">{body}</div>
        {clearRow}
      </PopoverContent>
    </Popover>
  );
}
